/** Matches C# VerificationResult enum: 0=Ok 1=Mismatch 2=Error 3=Info 4=NotApplicable */
export type VerificationResult = 0 | 1 | 2 | 3 | 4;

/** Summary data for a check-print request. */
export interface RequestSummary {
  requestId: number;
  arNumber: string;
  companyName: string | null;
  requestStatus: string | null;
  createdDate: string;
  createdBy: string | null;
  assignedTo: string | null;
  printSite: string | null;
  countryCode: string | null;
  sla: string | null;
  isManualCheck: boolean;
  isGarnCorp: boolean;
  isVisible: boolean;
  isProcessing: boolean;
}

/** Verification data for a single file within a request. */
export interface FileDiagnostic {
  fileId: number;
  fileName: string;
  fileNumber: string | null;
  totalChecks: number;
  totalDdas: number;
  totalAmount: number;
  currentStatus: string | null;
  expectedLocation: string | null;
  existsInObjectStorage: boolean | null;
  objectStorageSizeBytes: number | null;
  objectStorageSha256: string | null;
  existsOnSftp: boolean | null;
  sftpSizeBytes: number | null;
  sftpSha256: string | null;
  result: VerificationResult;
  errorMessage: string | null;
}

/** Single entry in the ingestion processing trail for a request. */
export interface IngestionTrail {
  source: string;
  processId: string | null;
  arNumber: string | null;
  fileName: string | null;
  processStatus: string | null;
  createdDate: string;
}

/** Single row comparing a field between CheckPrint and CheckLog databases. */
export interface DataParityRow {
  field: string;
  checkPrintValue: string | null;
  checkLogValue: string | null;
  isMatch: boolean;
}

/** Data parity comparison result between CheckPrint and CheckLog databases. */
export interface DataParity {
  isError: boolean;
  errorMessage: string | null;
  rows: DataParityRow[];
}

/** Email confirmation and NetPay callback status for a request. */
export interface EmailCallback {
  confirmationEmailSent: boolean;
  confirmationEmailTimestamp: string | null;
  netPayCallbackStatus: string | null;
  netPayCallbackTimestamp: string | null;
  hasAcknowledgementFailures: boolean;
  acknowledgementFailureCount: number;
}

/**
 * Full diagnostic result for a single request.
 * API returns "request" (not "requestSummary") at the root level.
 */
export interface DiagnosticResult {
  request: RequestSummary;
  files: FileDiagnostic[];
  ingestionTrail: IngestionTrail[];
  dataParity: DataParity;
  emailCallbackStatus: EmailCallback;
}

export interface RequestDiagnosticResult {
  request: RequestSummary;
}

export interface FileDiagnosticResult {
  files: FileDiagnostic[];
}

export interface IngestionTrailDiagnosticResult {
  ingestionTrail: IngestionTrail[];
}

export interface DataParityDiagnosticResult {
  dataParity: DataParity;
}

export interface EmailCallbackDiagnosticResult {
  emailCallbackStatus: EmailCallback;
}

/** Request that is stuck in a processing state. */
export interface StuckRequest {
  requestId: number;
  arNumber: string | null;
  stuckFlag: string;
  fileId: number | null;
  stuckSince: string;
  ageMinutes: number;
  createdBy: string | null;
}

/**
 * Summary of stuck requests with pagination.
 * API returns "isVisibleOnGridCount" (not "isVisibleCount").
 */
export interface StuckRequestsSummary {
  isVisibleOnGridCount: number;
  isProcessingCount: number;
  isReady2ProcessCount: number;
  isReady4UluroCount: number;
  details: StuckRequest[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

/** File rejected during ingestion. */
export interface RejectedFile {
  source: string;
  fileName: string | null;
  arNumber: string | null;
  requestId: number | null;
  rejectionReason: string | null;
  timestamp: string;
}

/** Heartbeat data for a registered Windows service. */
export interface ServiceHeartbeat {
  serviceName: string;
  lastHeartbeat: string;
  status: string;
  machineName: string | null;
  machineIP: string | null;
  iterationDurationMs: number | null;
  itemsProcessed: number | null;
  lastErrorMessage: string | null;
  lastErrorTimestamp: string | null;
  serviceVersion: string | null;
  intervalMinutes: number | null;
  minutesSinceHeartbeat: number;
  isStale: boolean;
}

/** Result of a single infrastructure connectivity check. */
export interface InfrastructureCheck {
  name: string;
  category: string;
  description: string;
  status: string;
  responseMs: number;
  errorMessage: string | null;
}

/** Aggregated system health data returned by the health endpoint. */
export interface SystemHealth {
  stuckRequests: StuckRequestsSummary;
  rejectedFiles: RejectedFile[];
  serviceHeartbeats: ServiceHeartbeat[];
  infrastructureChecks: InfrastructureCheck[];
}
/** Result returned after fixing IsVisibleOnGrid flag on stuck requests. */
export interface FixVisibilityResult {
  fixedCount: number;
}

export enum InfraName {
  FpsDatabase = 'FpsDatabase',
  CheckLogDatabase = 'CheckLogDatabase',
  Salesforce = 'Salesforce',
  ObjectStorage = 'ObjectStorage',
  LaunchDarkly = 'LaunchDarkly',
  Sftp = 'Sftp',
  Smtp = 'Smtp',
  UkgAuthN = 'UkgAuthN',
}

export enum InfraCategory {
  Database = 'Database',
  ObjectStorage = 'ObjectStorage',
  Sftp = 'Sftp',
  External = 'External',
  Email = 'Email',
}

export const InfraCategoryNameMap = {
  [InfraCategory.Database]: [InfraName.FpsDatabase, InfraName.CheckLogDatabase],
  [InfraCategory.ObjectStorage]: [InfraName.ObjectStorage],
  [InfraCategory.Sftp]: [InfraName.Sftp],
  [InfraCategory.External]: [InfraName.UkgAuthN],
  [InfraCategory.Email]: [InfraName.Smtp],
};

/** Single file item in storage (SFTP or Object Storage). */
export interface StorageFileItem {
  name: string;
  fullPath: string;
  sizeBytes: number;
  lastModified: string | null;
  isDirectory: boolean;
}

/** Group of files within a single SFTP folder. */
export interface SftpFolderGroup {
  folderPath: string;
  folderType: string; // 'Main' | 'EEPay' | 'Garn'
  files: StorageFileItem[];
  error: string | null;
}

/** Single SFTP server with its folders and files. */
export interface SftpServerGroup {
  hostName: string;
  address: string;
  folders: SftpFolderGroup[];
  error: string | null;
}

/** Single Object Storage folder with files. */
export interface ObjectStorageFolder {
  folderPath: string;
  files: StorageFileItem[];
  error: string | null;
}

/** Complete storage files result from diagnostics API. */
export interface StorageFilesResult {
  inputSftpServers: SftpServerGroup[];
  outputSftpServers: SftpServerGroup[];
  objectStorageFolders: ObjectStorageFolder[];
}

/** SFTP storage result from separate endpoint (input or output). */
export interface SftpStorageListResult {
  sftpServers: SftpServerGroup[];
}

/** Object storage result from separate endpoint. */
export interface ObjectStorageFilesResult {
  objectStorageFolders: ObjectStorageFolder[];
}

/** Single file entry from the SFTP snapshot. */
export interface SftpSnapshotFile {
  name: string;
  sizeBytes: number | null;
  lastModifiedAt: string | null;
}

/** Single folder from the SFTP snapshot. */
export interface SftpSnapshotFolder {
  folderMappingId: number;
  folderLabel: string;
  folderPath: string;
  files: SftpSnapshotFile[];
}

/** Single server entry from the SFTP snapshot. */
export interface SftpSnapshotServer {
  hostName: string;
  address: string;
  folders: SftpSnapshotFolder[];
}

/** Result from GET /api/v1/diagnostics/storage-files/sftp/snapshots. */
export interface SftpAllSnapshotsResult {
  inputServers: SftpSnapshotServer[];
  outputServers: SftpSnapshotServer[];
}
